import { useCallback } from "react";

import { useStableRef } from "../../hooks/useStableRef.js";
import { useSelector, useStore } from "../../redux/hooks";
import {
  GridRowMP,
  hexVersionMPSelectors,
} from "../../redux/slices/hexVersionMPSlice";
import { useProjectContext } from "../../util/projectContext";

export interface UseGridRowsSelectorArgs<T> {
  selector: (gridRows: GridRowMP[]) => T;
  equalityFn?: (left: T, right: T) => boolean;
}

export function useGridRowsSelector<T>({
  equalityFn,
  selector,
}: UseGridRowsSelectorArgs<T>): T {
  const { hexVersionId } = useProjectContext();

  return useSelector((state) => {
    const gridRowsState = hexVersionMPSelectors
      .getGridRowSelectors(hexVersionId)
      .selectAll(state);

    if (gridRowsState == null) {
      throw new Error(
        `Missing grid rows state for hex version: ${hexVersionId}`,
      );
    }

    return selector(gridRowsState);
  }, equalityFn);
}

export interface UseGridRowsGetterArgs<TArgs extends unknown[], TResult> {
  selector?: (gridRows: GridRowMP[], ...args: TArgs) => TResult;
}

export type UseGridRowsGetterResult<TArgs extends unknown[], TResult> = (
  ...args: TArgs
) => TResult;

export function useGridRowsGetter<
  TArgs extends unknown[],
  TResult = GridRowMP[],
>({
  selector,
}: UseGridRowsGetterArgs<TArgs, TResult> = {}): UseGridRowsGetterResult<
  TArgs,
  TResult
> {
  const { hexVersionId } = useProjectContext();
  const store = useStore();
  const selectorRef = useStableRef(selector);

  return useCallback(
    (...args: TArgs) => {
      const gridRows = hexVersionMPSelectors
        .getGridRowSelectors(hexVersionId)
        .selectAll(store.getState());

      if (gridRows == null) {
        throw new Error(
          `Missing grid rows state for hex version: ${hexVersionId}`,
        );
      }

      // this cannot be conditionally chained/nullish coalesced since
      // the provided selector may intentionally return undefined
      return selectorRef.current
        ? selectorRef.current(gridRows, ...args)
        : (gridRows as unknown as TResult);
    },
    [hexVersionId, store, selectorRef],
  );
}
